import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { InviteAPI } from '../services/api';
import AdminDataService, { User, UserActivity } from '../services/AdminDataService';
import { AlertService } from '../services/AlertService';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { UserFilters } from '../components/UserFilters';
import { LoadingScreen } from '../components/LoadingScreen';
import { Pagination } from '../components/Pagination';
import { useUserFilters } from '../hooks/useUserFilters';
import { useLoading } from '../hooks/useLoading';
import { hasPermission, getRoleDisplayName, getRoleColor } from '../utils/rolePermissions';
import { useAuth } from '../context/AuthContext';
import { useForm } from 'react-hook-form';

interface Invite {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  is_used: boolean;
  created_at: string;
}


interface CreateInviteForm {
  email: string;
  role: string;
  supervisor_id?: string;
}

/**
 * UserManagement Page - Complete standalone page
 * 
 * Purpose: Full-featured user management with invite system
 * Features: User management, invite creation/management, tabs, full page layout
 * Layout: Uses DashboardLayout with header, sidebar, navigation
 * 
 * Used in: Main app routing at /user-management
 * Route: /user-management (standalone page)
 * CSS: Uses .user-management scope, inline styles for page-specific elements
 * State: Independent from components version, includes invite management
 * 
 * IMPORTANT: This is the ONLY place that should handle invite functionality.
 * Admin stats belong in components/UserManagement.tsx (AdminPanel)
 */
export const UserManagement: React.FC = () => {
  const { user } = useAuth();
  // Only admins can see / change who is view only.
  const isAdminViewer = user?.role === 'admin';
  const [roleFilteredUsers, setRoleFilteredUsers] = useState<User[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [activeTab, setActiveTab] = useState<'users' | 'invites'>('users');
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  
  // Loading state management
  const { isLoading, withLoading, loadingMessage } = useLoading('Initializing user management...');
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState<'edit' | 'delete' | 'activities' | null>(null);
  const [editFormData, setEditFormData] = useState({ role: '', supervisor_id: '' });
  const [availableRoles, setAvailableRoles] = useState<{ value: string; label: string }[]>([]);
  const [availableSupervisors, setAvailableSupervisors] = useState<{ id: string; name: string; role: string }[]>([]);
  const [editAvailableSupervisors, setEditAvailableSupervisors] = useState<{ id: string; name: string; role: string }[]>([]);
  const [allSupervisors, setAllSupervisors] = useState<{ id: string; name: string; role: string }[]>([]);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    confirmButtonClass?: string;
    onConfirm: () => void;
    loading?: boolean;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const inviteForm = useForm<CreateInviteForm>();
  const adminService = AdminDataService.getInstance();
  const alertService = AlertService;

  // Watch the selected role for supervisor fetching
  const selectedRole = inviteForm.watch('role');

  // Helper function to check if role requires supervisor
  const roleRequiresSupervisor = (role: string): boolean => {
    const rolesRequiringSupervisor = ['agent', 'processor_agent', 'sales_team_lead', 'processor_team_lead', 'floor_manager', 'director'];
    return rolesRequiringSupervisor.includes(role);
  };

  // Helper function to check if user is admin (protected from changes)
  const isAdminUser = (userRole: string): boolean => {
    return userRole === 'admin';
  };

  // Initialize filter system with role-filtered users
  const filterOptions = {
    availableRoles: availableRoles.map(role => role.value),
    availableSupervisors: allSupervisors
  };
  
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredUsers,
    uniqueRoles,
    filterStats
  } = useUserFilters(roleFilteredUsers, filterOptions);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Paginated users
  const paginatedUsers = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Check permissions
  const canManageUsers = hasPermission(user?.role, 'canManageUsers');

  const loadData = useCallback(async () => {
    await withLoading(async () => {
      try {
        console.log('🔍 Loading users and invites...');
        const [usersData, invitesData] = await Promise.all([
          adminService.getUsers(true), // Get all users from AdminDataService
          InviteAPI.getInvites()
        ]);
        
        console.log('📊 Raw users data:', usersData);
        console.log('📧 Invites data:', invitesData);
        console.log('👤 Current user:', user);
        
        // Filter out the current logged-in user
        const filteredUsers = usersData.filter(u => u.id !== user?.id);
        console.log('🎯 Filtered users (excluding current user):', filteredUsers);
        
        setRoleFilteredUsers(filteredUsers);
        setInvites(invitesData);
        
        // Also update supervisor list as user changes might affect it
        await loadAllSupervisors();
      } catch (error: any) {
        console.error('❌ Failed to load data:', error);
        throw error; // Re-throw to let withLoading handle it properly
      }
    }, 'Loading user data and statistics...');
  }, [withLoading, adminService, user]);

  useEffect(() => {
    if (!canManageUsers) return;
    
    loadData();
    loadAvailableRoles();
    loadAllSupervisors();
  }, [canManageUsers, loadData]);

  // Fetch supervisors when role changes
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        if (selectedRole && roleRequiresSupervisor(selectedRole)) {
          const supervisors = await adminService.getAvailableSupervisors(selectedRole);
          setAvailableSupervisors(
            supervisors.map(user => ({
              id: user.id,
              name: user.full_name || user.username,
              role: user.role
            }))
          );
        } else {
          setAvailableSupervisors([]);
        }
      } catch (err) {
        console.warn('Could not fetch available supervisors:', err);
        setAvailableSupervisors([]);
      }
    };

    if (selectedRole) {
      fetchSupervisors();
    }
  }, [selectedRole, adminService]);

  // Fetch supervisors when edit role changes
  useEffect(() => {
    const fetchEditSupervisors = async () => {
      try {
        if (editFormData.role && roleRequiresSupervisor(editFormData.role)) {
          const supervisors = await adminService.getAvailableSupervisors(editFormData.role);
          setEditAvailableSupervisors(
            supervisors.map(user => ({
              id: user.id,
              name: user.full_name || user.username,
              role: user.role
            }))
          );
        } else {
          setEditAvailableSupervisors([]);
        }
      } catch (err) {
        console.warn('Could not fetch available supervisors for edit:', err);
        setEditAvailableSupervisors([]);
      }
    };

    if (showModal === 'edit' && editFormData.role) {
      fetchEditSupervisors();
    }
  }, [editFormData.role, showModal, adminService]);

  const loadAvailableRoles = async () => {
    try {
      const roles = await adminService.getUserRoles();
      setAvailableRoles(roles);
    } catch (error: any) {
      console.error('Failed to load available roles:', error);
      alertService.error('Failed to load available roles: ' + error.message);
    }
  };

  const loadAllSupervisors = async () => {
    try {
      // Get all users and filter for potential supervisors
      const users = await adminService.getUsers(true);
      const supervisorRoles = ['sales_team_lead', 'processor_team_lead', 'floor_manager', 'director', 'admin'];
      const supervisors = users.filter(user => 
        supervisorRoles.includes(user.role) && user.is_active
      ).map(user => ({
        id: user.id,
        name: user.full_name || user.username,
        role: user.role
      }));
      setAllSupervisors(supervisors);
    } catch (error: any) {
      console.error('Failed to load supervisors for filtering:', error);
      // Don't show error to user as this is non-critical for basic functionality
    }
  };

  const handleCreateInvite = async (data: CreateInviteForm) => {
    // Validate supervisor requirement
    if (roleRequiresSupervisor(data.role) && !data.supervisor_id) {
      if (availableSupervisors.length === 0) {
        alertService.error(`Cannot create ${data.role} invite: No supervisors available. Please create a supervisor first.`);
        return;
      } else {
        alertService.error(`Supervisor is required for ${data.role} role. Please select a supervisor.`);
        return;
      }
    }

    await withLoading(async () => {
      try {
        await InviteAPI.createInvite(data);
        alertService.success('Invite sent successfully!');
        inviteForm.reset();
        setShowCreateInvite(false);
      } catch (error: any) {
        console.error('Failed to create invite:', error);
        alertService.error('Failed to send invite: ' + (error.message || 'Unknown error'));
        throw error; // Re-throw to let withLoading handle it properly
      }
    }, 'Sending invite...');
    
    // Reload data after the invite is sent (outside of withLoading to avoid message conflict)
    await loadData();
  };

  // Note: handleDeleteUser function available for future use
  // const handleDeleteUser = async (userId: string) => {
  //   if (!window.confirm('Are you sure you want to delete this user?')) return;
  //   
  //   try {
  //     await AdminAPI.deleteUser(userId);
  //     loadData();
  //   } catch (error) {
  //     console.error('Failed to delete user:', error);
  //   }
  // };

  const handleDeleteInvite = (inviteId: string, inviteEmail: string) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Delete Invite',
      message: `Are you sure you want to delete the invite for "${inviteEmail}"? This action cannot be undone.`,
      confirmText: 'Delete Invite',
      confirmButtonClass: 'btn-danger',
      onConfirm: () => confirmDeleteInvite(inviteId)
    });
  };

  const confirmDeleteInvite = async (inviteId: string) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await InviteAPI.deleteInvite(inviteId);
      alertService.success('Invite deleted successfully');
      await loadData();
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    } catch (error: any) {
      alertService.error(`Failed to delete invite: ${error.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  // User Management Functions
  const handleEditUser = async (user: User) => {
    try {
      // Roles are already loaded on component mount, no need to reload
      if (availableRoles.length === 0) {
        await loadAvailableRoles();
      }
      setSelectedUser(user);
      setEditFormData({ 
        role: user.role, 
        supervisor_id: user.supervisor_id || '' 
      });
      
      // Fetch available supervisors for the current user's role
      if (roleRequiresSupervisor(user.role)) {
        try {
          const supervisors = await adminService.getAvailableSupervisors(user.role);
          setEditAvailableSupervisors(
            supervisors.map(supervisor => ({
              id: supervisor.id,
              name: supervisor.full_name || supervisor.username,
              role: supervisor.role
            }))
          );
        } catch (err) {
          console.warn('Could not fetch available supervisors for edit:', err);
          setEditAvailableSupervisors([]);
        }
      } else {
        setEditAvailableSupervisors([]);
      }
      
      setShowModal('edit');
    } catch (error: any) {
      alertService.error('Failed to load user data: ' + error.message);
    }
  };

  const handleResetPassword = (user: User) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Reset User Password',
      message: `Are you sure you want to reset the password for user "${user.username}" (${user.full_name || 'N/A'})? This will:\n\n• Send reset instructions to ${user.email}\n• Require the user to set a new password on next login\n\nThe user will receive an email with password reset instructions.`,
      confirmText: 'Reset Password',
      confirmButtonClass: 'btn-accent',
      onConfirm: () => confirmResetPassword(user)
    });
  };

  const confirmResetPassword = async (user: User) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const result = await adminService.resetUserPassword(user.id);
      alertService.success(`Password reset initiated successfully for ${user.username}. ${result.message}`);
      await loadData();
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    } catch (error: any) {
      alertService.error(`Failed to reset password: ${error.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  const handleToggleUserStatus = (user: User) => {
    // Prevent deactivating admin users
    if (isAdminUser(user.role)) {
      alertService.error('Admin users cannot be deactivated for security reasons. Admin accounts must remain active.');
      return;
    }

    const action = user.is_active ? 'deactivate' : 'activate';
    const status = user.is_active ? 'inactive' : 'active';
    
    setConfirmationDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} user "${user.username}" (${user.full_name || 'N/A'})? This will change their status to ${status} and ${user.is_active ? 'prevent them from accessing' : 'allow them to access'} the system.`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      confirmButtonClass: user.is_active ? 'btn-warning' : 'btn-primary',
      onConfirm: () => confirmToggleUserStatus(user)
    });
  };

  const confirmToggleUserStatus = async (user: User) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await adminService.toggleUserStatus(user.id);
      await loadData();
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      alertService.success(`User ${user.username} has been ${user.is_active ? 'deactivated' : 'activated'} successfully.`);
    } catch (error: any) {
      alertService.error(`Failed to ${user.is_active ? 'deactivate' : 'activate'} user: ${error.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  const handleToggleViewOnly = (target: User) => {
    const turnOn = !target.view_only;
    setConfirmationDialog({
      isOpen: true,
      title: turnOn ? 'Make View Only' : 'Remove View Only',
      message: turnOn
        ? `Make "${target.username}" (${target.full_name || 'N/A'}) view only? They will still see everything their role shows (and can comment, chat, download and reveal values their role allows), but can't create, approve, edit, void/refund, assign or change anything. Takes effect within 30 seconds.`
        : `Give "${target.username}" (${target.full_name || 'N/A'}) back their normal access for the ${getRoleDisplayName(target.role)} role?`,
      confirmText: turnOn ? 'Make View Only' : 'Remove View Only',
      confirmButtonClass: turnOn ? 'btn-warning' : 'btn-primary',
      onConfirm: async () => {
        setConfirmationDialog(prev => ({ ...prev, loading: true }));
        try {
          await adminService.setViewOnly(target.id, turnOn);
          await loadData();
          alertService.success(`${target.username} is ${turnOn ? 'now view only' : 'no longer view only'}.`);
        } catch (error: any) {
          alertService.error(error.message);
        }
        setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };

  const handleViewActivities = async (user: User) => {
    try {
      setSelectedUser(user);
      const userActivities = await adminService.getUserActivities(user.id, true);
      setActivities(userActivities);
      setShowModal('activities');
    } catch (error: any) {
      alertService.error(`Failed to load activities: ${error.message}`);
    }
  };

  const handleDeleteUser = (user: User) => {
    // Prevent deletion of admin users
    if (isAdminUser(user.role)) {
      alertService.error('Admin users cannot be deleted for security reasons. Admin accounts are permanent.');
      return;
    }

    setConfirmationDialog({
      isOpen: true,
      title: 'Remove User Account',
      message: `Are you sure you want to remove user "${user.username}" (${user.full_name || 'N/A'}) from active management? This action will:\n\n• Deactivate the user account\n• Remove user from management interface\n• Revoke all active sessions\n• Preserve all data for audit purposes\n\nThe user will no longer be able to access the system, but their data will be retained.\n\nEmail: ${user.email}\nRole: ${user.role}\nStatus: ${user.is_active ? 'Active' : 'Inactive'}`,
      confirmText: 'Remove User',
      confirmButtonClass: 'btn-danger',
      onConfirm: () => confirmDeleteUser(user)
    });
  };

  const confirmDeleteUser = async (user: User) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await adminService.deleteUser(user.id);
      alertService.success(`User ${user.username} has been removed from active management.`);
      await loadData();
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    } catch (error: any) {
      alertService.error(`Failed to remove user: ${error.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  const confirmEdit = () => {
    if (!selectedUser) return;

    // Prevent editing admin users
    if (isAdminUser(selectedUser.role)) {
      alertService.error('Admin users cannot be modified for security reasons. Admin accounts are protected.');
      return;
    }

    const roleChanged = selectedUser.role !== editFormData.role;
    const supervisorChanged = (selectedUser.supervisor_id || '') !== editFormData.supervisor_id;
    
    if (!roleChanged && !supervisorChanged) {
      alertService.info('No changes detected.');
      return;
    }

    let changes = [];
    if (roleChanged) {
      changes.push(`• Change role from "${selectedUser.role}" to "${editFormData.role}"`);
    }
    if (supervisorChanged) {
      const currentSupervisor = selectedUser.supervisor_name || 'No supervisor';
      const newSupervisor = editAvailableSupervisors.find(s => s.id === editFormData.supervisor_id)?.name || 'No supervisor';
      changes.push(`• Change supervisor from "${currentSupervisor}" to "${newSupervisor}"`);
    }

    setConfirmationDialog({
      isOpen: true,
      title: 'Update User',
      message: `Are you sure you want to update user "${selectedUser.username}" (${selectedUser.full_name || 'N/A'})?\n\nChanges:\n${changes.join('\n')}\n\nThis will take effect immediately.`,
      confirmText: 'Update User',
      confirmButtonClass: 'btn-primary',
      onConfirm: () => confirmUpdateUser(selectedUser, editFormData.role, editFormData.supervisor_id)
    });
  };

  const confirmUpdateUser = async (user: User, newRole: string, supervisorId?: string) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const roleChanged = user.role !== newRole;
      const supervisorChanged = (user.supervisor_id || '') !== (supervisorId || '');
      
      // Update role if changed
      if (roleChanged) {
        await adminService.updateUserRole(user.id, newRole, user.is_active);
      }
      
      // Update supervisor if changed
      if (supervisorChanged) {
        await adminService.assignSupervisor(user.id, supervisorId || undefined);
      }
      
      let successMessage = 'User updated successfully!';
      if (roleChanged && supervisorChanged) {
        successMessage = `User role and supervisor updated successfully.`;
      } else if (roleChanged) {
        successMessage = `User role updated from ${user.role} to ${newRole}.`;
      } else if (supervisorChanged) {
        const newSupervisorName = editAvailableSupervisors.find(s => s.id === supervisorId)?.name || 'No supervisor';
        successMessage = `User supervisor updated to ${newSupervisorName}.`;
      }
      
      alertService.success(successMessage);
      setShowModal(null);
      await loadData();
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    } catch (error: any) {
      alertService.error(`Failed to update user: ${error.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  const confirmUpdateRole = async (user: User, newRole: string) => {
    // Keep this function for backward compatibility, but it's no longer used
    await confirmUpdateUser(user, newRole, user.supervisor_id || undefined);
  };

  const closeModal = () => {
    setShowModal(null);
    setSelectedUser(null);
    setActivities([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!canManageUsers) {
    return (
      <DashboardLayout>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access user management.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="user-management">
        <div className="page-title-section">
          <div className="title-with-actions">
            <div className="page-title-group">
              <h1 className="page-title">User Management</h1>
              <p className="page-subtitle">Manage users and invitations for your organization</p>
            </div>
            <div className="page-actions">
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateInvite(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 20.5H7C4 20.5 2 18.5 2 15.5V8.5C2 5.5 4 3.5 7 3.5H17C20 3.5 22 5.5 22 8.5V15.5C22 18.5 20 20.5 17 20.5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Send Invite
              </button>
              <button 
                onClick={loadData} 
                className="btn btn-outline-primary"
                disabled={isLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 12C22 6.48 17.52 2 12 2C10.02 2 8.17 2.51 6.56 3.41L2 2V8H8L5.99 5.99C7.14 5.34 8.53 5 12 5C15.87 5 19 8.13 19 12H22Z" fill="currentColor"/>
                  <path d="M2 12C2 17.52 6.48 22 12 22C13.98 22 15.83 21.49 17.44 20.59L22 22V16H16L18.01 18.01C16.86 18.66 15.47 19 12 19C8.13 19 5 15.87 5 12H2Z" fill="currentColor"/>
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* User Filters */}
        <UserFilters
          filters={filters}
          updateFilter={updateFilter}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          uniqueRoles={uniqueRoles}
          availableSupervisors={allSupervisors}
          filterStats={filterStats}
        />

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users ({roleFilteredUsers.length})
          </button>
          <button 
            className={`tab ${activeTab === 'invites' ? 'active' : ''}`}
            onClick={() => setActiveTab('invites')}
          >
            Invites ({invites.length})
          </button>
        </div>

        {isLoading ? (
          <LoadingScreen isVisible={true} message={loadingMessage} />
        ) : (
          <div className="content">
            {activeTab === 'users' && (
              <div className="users-table">
                <div className="table-header">
                  <h3>
                    Manageable Users 
                    {hasActiveFilters && (
                      <span className="filtered-indicator">
                        ({filteredUsers.length} of {roleFilteredUsers.length})
                      </span>
                    )}
                    {!hasActiveFilters && (
                      <span className="total-count">({filteredUsers.length})</span>
                    )}
                  </h3>
                  {user && (
                    <p className="table-subtitle">
                      Showing users you can manage as <strong>{user.role}</strong> (excluding yourself)
                      {hasActiveFilters && (
                        <span className="filter-active-indicator"> • Filters applied</span>
                      )}
                    </p>
                  )}
                </div>

                {filteredUsers.length === 0 ? (
                  <div className="empty-state">
                    {hasActiveFilters ? (
                      <p>No users match your current filters.</p>
                    ) : (
                      <>
                        <p>No users available for management.</p>
                        <p className="note">Users are created when they complete signup using an invite.</p>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <table>
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Full Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          {isAdminViewer && <th>View Only</th>}
                          <th>Last Login</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                    <tbody>
                      {paginatedUsers.map(user => (
                        <tr key={user.id}>
                          <td>{user.username}</td>
                          <td>{user.full_name || 'N/A'}</td>
                          <td>{user.email}</td>
                          <td>
                            <span 
                              className="role-badge"
                              style={{ backgroundColor: getRoleColor(user.role) }}
                            >
                              {getRoleDisplayName(user.role)}
                            </span>
                          </td>
                          <td>
                            <label className="toggle-switch" title={isAdminUser(user.role) ? 'Admin users cannot be deactivated' : `Click to ${user.is_active ? 'deactivate' : 'activate'} user`}>
                              <input
                                type="checkbox"
                                checked={user.is_active}
                                onChange={() => handleToggleUserStatus(user)}
                                disabled={isAdminUser(user.role)}
                              />
                              <span className="toggle-slider"></span>
                              <span className="toggle-text">
                                {user.is_active ? 'Active' : 'Inactive'}
                                {isAdminUser(user.role)}
                              </span>
                            </label>
                          </td>
                          {isAdminViewer && (
                            <td>
                              <label className="toggle-switch" title={isAdminUser(user.role) ? 'Admin users cannot be made view only' : `Click to ${user.view_only ? 'remove' : 'give'} view-only access`}>
                                <input
                                  type="checkbox"
                                  checked={!!user.view_only}
                                  onChange={() => handleToggleViewOnly(user)}
                                  disabled={isAdminUser(user.role)}
                                />
                                <span className="toggle-slider"></span>
                                <span className="toggle-text">{user.view_only ? 'View only' : 'Full'}</span>
                              </label>
                            </td>
                          )}
                          <td>{user.last_login ? formatDate(user.last_login) : 'Never'}</td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className={`btn btn-icon btn-icon-sm ${isAdminUser(user.role) ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                                onClick={() => handleEditUser(user)}
                                title={isAdminUser(user.role) ? 'Admin users cannot be edited' : 'Edit Role'}
                                disabled={isAdminUser(user.role)}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M11 2H9C4.5 2 2 4.5 2 9V15C2 19.5 4.5 22 9 22H15C19.5 22 22 19.5 22 15V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M16.04 3.02001L8.16 10.9C7.86 11.2 7.56 11.79 7.5 12.22L7.07 15.23C6.91 16.32 7.68 17.08 8.77 16.93L11.78 16.5C12.2 16.44 12.79 16.14 13.1 15.84L20.98 7.96001C22.34 6.60001 22.98 5.02001 20.98 3.02001C18.98 1.02001 17.4 1.66001 16.04 3.02001Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M14.91 4.1499C15.58 6.5399 17.45 8.4099 19.85 9.0899" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button 
                                className="btn btn-icon btn-icon-sm btn-accent"
                                onClick={() => handleResetPassword(user)}
                                title="Reset Password"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6 10V8C6 5.79 7.79 4 10 4H14C16.21 4 18 5.79 18 8V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M15.9965 16H16.0054" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M11.9965 16H12.0054" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M7.99651 16H8.00549" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button 
                                className="btn btn-icon btn-icon-sm btn-secondary"
                                onClick={() => handleViewActivities(user)}
                                title="View Activity"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42 13.98 8.42 12C8.42 10.02 10.02 8.42 12 8.42C13.98 8.42 15.58 10.02 15.58 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.4C18.82 5.8 15.53 3.72 12 3.72C8.47 3.72 5.18 5.8 2.89 9.4C1.99 10.81 1.99 13.18 2.89 14.59C5.18 18.19 8.47 20.27 12 20.27Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button 
                                className={`btn btn-icon btn-icon-sm ${isAdminUser(user.role) ? 'btn-outline-secondary' : 'btn-danger'}`}
                                onClick={() => handleDeleteUser(user)}
                                title={isAdminUser(user.role) ? 'Admin users cannot be deleted' : 'Delete User'}
                                disabled={isAdminUser(user.role)}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M21 5.98C17.67 5.65 14.32 5.48 10.98 5.48C9 5.48 7.02 5.58 5.04 5.78L3 5.98" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M10.33 16.5H13.66" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M9.5 12.5H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <Pagination
                      currentPage={currentPage}
                      totalItems={filteredUsers.length}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      pageSizeOptions={[10, 20, 30, 50, 100]}
                    />
                  </>
                )}
              </div>
            )}

            {activeTab === 'invites' && (
              <div className="invites-table">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Expires</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites
                      .filter(invite => {
                        const matchesSearch = filters.globalSearch === '' || 
                          invite.email.toLowerCase().includes(filters.globalSearch.toLowerCase()) ||
                          invite.role.toLowerCase().includes(filters.globalSearch.toLowerCase());
                        const matchesRole = filters.role === '' || invite.role === filters.role;
                        const matchesEmail = filters.email === '' || 
                          invite.email.toLowerCase().includes(filters.email.toLowerCase());
                        return matchesSearch && matchesRole && matchesEmail;
                      })
                      .map(invite => (
                      <tr key={invite.id}>
                        <td>{invite.email}</td>
                        <td>
                          <span 
                            className="role-badge"
                            style={{ backgroundColor: getRoleColor(invite.role) }}
                          >
                            {getRoleDisplayName(invite.role)}
                          </span>
                        </td>
                        <td>
                          <span className={`status ${invite.is_used ? 'used' : 'pending'}`}>
                            {invite.is_used ? 'Used' : 'Pending'}
                          </span>
                        </td>
                        <td>{new Date(invite.expires_at).toLocaleDateString()}</td>
                        <td>{new Date(invite.created_at).toLocaleDateString()}</td>
                        <td>
                          {!invite.is_used && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteInvite(invite.id, invite.email)}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {/* Create Invite Modal */}
        {showCreateInvite && (
          <div className="modal-overlay" onClick={() => setShowCreateInvite(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h2>Send Invite</h2>
              <form onSubmit={inviteForm.handleSubmit(handleCreateInvite)}>
                <div className="form-group">
                  <label>Email*</label>
                  <input
                    type="email"
                    {...inviteForm.register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@erpamperedeals\.com$/i,
                        message: 'Email must be from @erpamperedeals.com domain'
                      }
                    })}
                  />
                  {inviteForm.formState.errors.email && (
                    <span className="error">{inviteForm.formState.errors.email.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Role*</label>
                  <select {...inviteForm.register('role', { required: 'Role is required' })}>
                    <option value="">Select Role</option>
                    {availableRoles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {inviteForm.formState.errors.role && (
                    <span className="error">{inviteForm.formState.errors.role.message}</span>
                  )}
                </div>

                {roleRequiresSupervisor(selectedRole) && (
                  <div className="form-group">
                    <label>
                      Supervisor* 
                      {availableSupervisors.length === 0 && selectedRole && (
                        <span style={{ fontSize: '0.8em', color: '#d32f2f', fontWeight: 'normal' }}>
                          {' '}- No supervisors available! Cannot create this role.
                        </span>
                      )}
                    </label>
                    <select 
                      {...inviteForm.register('supervisor_id', { 
                        required: roleRequiresSupervisor(selectedRole) ? 'Supervisor is required for this role' : false 
                      })}
                      disabled={availableSupervisors.length === 0}
                    >
                      <option value="">
                        {availableSupervisors.length === 0 
                          ? 'No supervisors available' 
                          : 'Select a supervisor (required)'
                        }
                      </option>
                      {availableSupervisors.map(supervisor => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.name} - {supervisor.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                    {inviteForm.formState.errors.supervisor_id && (
                      <span className="error">{inviteForm.formState.errors.supervisor_id.message}</span>
                    )}
                    {availableSupervisors.length > 0 && (
                      <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '4px' }}>
                        The invited user will be automatically assigned to the selected supervisor upon signup.
                      </small>
                    )}
                    {availableSupervisors.length === 0 && roleRequiresSupervisor(selectedRole) && (
                      <small style={{ color: '#d32f2f', fontSize: '0.875rem', display: 'block', marginTop: '4px' }}>
                        ⚠️ You must create a supervisor before inviting users for this role.
                      </small>
                    )}
                  </div>
                )}

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCreateInvite(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 20.5H7C4 20.5 2 18.5 2 15.5V8.5C2 5.5 4 3.5 7 3.5H17C20 3.5 22 5.5 22 8.5V15.5C22 18.5 20 20.5 17 20.5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Send Invite
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showModal === 'edit' && selectedUser && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Edit User: {selectedUser.username}</h2>
              <div className="form-group">
                <label>Role:</label>
                <select 
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({...editFormData, role: e.target.value, supervisor_id: ''})}
                >
                  {availableRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>

              {roleRequiresSupervisor(editFormData.role) && (
                <div className="form-group">
                  <label>
                    Supervisor:
                    {editAvailableSupervisors.length === 0 && editFormData.role && (
                      <span style={{ fontSize: '0.8em', color: '#d32f2f', fontWeight: 'normal' }}>
                        {' '}- No supervisors available
                      </span>
                    )}
                  </label>
                  <select 
                    value={editFormData.supervisor_id}
                    onChange={(e) => setEditFormData({...editFormData, supervisor_id: e.target.value})}
                    disabled={editAvailableSupervisors.length === 0}
                  >
                    <option value="">No supervisor</option>
                    {editAvailableSupervisors.map(supervisor => (
                      <option key={supervisor.id} value={supervisor.id}>
                        {supervisor.name} - {supervisor.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                  {editAvailableSupervisors.length > 0 && (
                    <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '4px' }}>
                      Select a supervisor for this user, or leave as "No supervisor" if not needed.
                    </small>
                  )}
                </div>
              )}
              <div className="form-group">
                <p style={{fontSize: '0.9rem', color: 'var(--text-secondary)', fontStyle: 'italic'}}>
                  <strong>Note:</strong> Use the toggle switch in the table to change active/inactive status.
                </p>
              </div>
              <div className="form-actions">
                <button onClick={confirmEdit} className="btn btn-primary">Save Changes</button>
                <button onClick={closeModal} className="btn btn-outline-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* User Activities Modal */}
        {showModal === 'activities' && selectedUser && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal large-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Activity Log: {selectedUser.username}</h2>
              <div className="activities-list">
                {activities.length > 0 ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>Details</th>
                        <th>IP Address</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map(activity => (
                        <tr key={activity.id}>
                          <td>{activity.action}</td>
                          <td>{activity.details}</td>
                          <td>{activity.ip_address || 'N/A'}</td>
                          <td>{formatDate(activity.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No activities found for this user.</p>
                )}
              </div>
              <div className="form-actions">
                <button onClick={closeModal} className="btn btn-outline-secondary">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={confirmationDialog.isOpen}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          confirmText={confirmationDialog.confirmText}
          confirmButtonClass={confirmationDialog.confirmButtonClass}
          loading={confirmationDialog.loading}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={() => setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        />
      </div>

      <style>{`
        .user-management {
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-title-section {
          margin-bottom: 2rem;
        }

        .title-with-actions {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 2rem;
          background: var(--bg-card);
          padding: 1.5rem 2rem;
          border-radius: 12px;
          border: 1px solid var(--border-primary);
          box-shadow: 0 2px 4px var(--shadow-light);
        }

        .page-title-group {
          flex: 1;
        }

        .page-title {
          margin: 0 0 0.5rem 0;
          font-size: 1.875rem;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.2;
        }

        .page-subtitle {
          margin: 0;
          color: var(--text-secondary);
          font-size: 1rem;
          line-height: 1.4;
        }

        .page-actions {
          display: flex;
          gap: 1rem;
          flex-shrink: 0;
        }

        .filters-panel {
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          display: flex;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .search-input,
        .role-filter {
          padding: 0.5rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          min-width: 200px;
        }

        .search-input:focus,
        .role-filter:focus {
          outline: none;
          border-color: var(--primary);
        }

        .tabs {
          border-bottom: 2px solid var(--border-primary);
          margin-bottom: 2rem;
        }

        .tab {
          background: none;
          border: none;
          padding: 1rem 2rem;
          cursor: pointer;
          color: var(--text-secondary);
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .tab.active {
          color: var(--primary);
          border-bottom-color: var(--primary);
        }

        .tab:hover {
          color: var(--text-primary);
        }

        table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        th, td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid var(--border-primary);
        }

        th {
          background: var(--bg-secondary);
          font-weight: 600;
          color: var(--text-primary);
        }

        td {
          color: var(--text-secondary);
        }

        .role-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          color: white;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status {
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status.active {
          background: #d4edda;
          color: #155724;
        }

        .status.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status.pending {
          background: #fff3cd;
          color: #856404;
        }

        .status.used {
          background: #d1ecf1;
          color: #0c5460;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 2rem;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid var(--border-primary);
        }

        .modal h2 {
          margin-top: 0;
          color: var(--text-primary);
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--input-border);
          border-radius: 4px;
          background: var(--input-bg);
          color: var(--text-primary);
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--input-focus);
          box-shadow: var(--shadow-focus);
        }

        .error {
          color: var(--input-error);
          font-size: 0.875rem;
          margin-top: 0.25rem;
          display: block;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2rem;
        }

        .info-message {
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          border-left: 4px solid var(--primary);
        }

        .info-message p {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
        }

        .info-message p:last-child {
          margin-bottom: 0;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        /* Toggle Switch */
        .toggle-switch {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          user-select: none;
        }

        .toggle-switch input[type="checkbox"] {
          display: none;
        }

        .toggle-slider {
          position: relative;
          width: 44px;
          height: 24px;
          background-color: var(--danger);
          border-radius: 24px;
          transition: background-color 0.3s ease;
        }

        .toggle-slider::before {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 20px;
          height: 20px;
          background-color: white;
          border-radius: 50%;
          transition: transform 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .toggle-switch input[type="checkbox"]:checked + .toggle-slider {
          background-color: var(--success);
        }

        .toggle-switch input[type="checkbox"]:checked + .toggle-slider::before {
          transform: translateX(20px);
        }

        .toggle-text {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          min-width: 50px;
        }

        .toggle-switch:hover .toggle-slider {
          box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
        }

        /* Large Modal */
        .large-modal {
          max-width: 800px !important;
          max-height: 90vh;
        }

        .activities-list {
          max-height: 400px;
          overflow-y: auto;
        }

        /* Table Header Styles */
        .table-header {
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .table-header h3 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 1.25rem;
          font-weight: 600;
        }

        .table-subtitle {
          margin: 0;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .table-subtitle strong {
          color: var(--primary);
          font-weight: 500;
        }

        /* Empty State Styles */
        .empty-state {
          text-align: center;
          padding: 3rem 2rem;
          color: var(--text-secondary);
          background: var(--bg-card);
          border-radius: 8px;
          border: 1px solid var(--border-primary);
        }

        .empty-state p {
          margin: 0 0 1rem 0;
          font-size: 1rem;
        }

        .empty-state .note {
          font-size: 0.875rem;
          color: var(--text-muted);
          font-style: italic;
        }

        /* Filter Indicators */
        .filtered-indicator {
          color: var(--warning);
          font-weight: 500;
          font-size: 0.9rem;
        }

        .total-count {
          color: var(--text-secondary);
          font-weight: 400;
          font-size: 0.9rem;
        }

        .filter-active-indicator {
          color: var(--primary);
          font-weight: 500;
          font-size: 0.8rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .title-with-actions {
            flex-direction: column;
            align-items: stretch;
            gap: 1.5rem;
            padding: 1.25rem 1.5rem;
          }

          .page-title {
            font-size: 1.5rem;
          }

          .page-actions {
            justify-content: flex-start;
          }
        }

        @media (max-width: 480px) {
          .title-with-actions {
            padding: 1rem;
          }

          .page-title {
            font-size: 1.375rem;
          }

          .page-actions {
            flex-direction: column;
            gap: 0.75rem;
          }
        }

      `}</style>
    </DashboardLayout>
  );
};